import React from "react";
import { Check, ArrowRight, Zap, ChartBar, LayoutTemplate, RefreshCw } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: "optimization",
  title: "Technological Optimization",
  description: "We refine digital workflows and internal infrastructures to ensure peak operational performance. Our systematic approach eliminates bottlenecks and scales your technical capabilities in alignment with Seattle's competitive landscape.",
  features: ["Workflow automation & streamlining", "Infrastructure performance auditing", "Systems integration architecture", "Scalability & capacity planning"],
  badge: "01 / CORE",
  badgeVariant: "default",
  icon: LayoutTemplate,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1619657/img/web_design_showcase.png",
  cta: "Optimize Strategy",
  link: "/OptimizationStrategyPage"
}, {
  id: "advisory",
  title: "Strategic Business Advisory",
  description: "High-level consulting focused on the intersection of modern technology and organizational efficiency. We serve as a versatile foundation for professional service delivery, providing the insight needed for sustainable growth.",
  features: ["Digital transformation roadmaps", "Strategic resource allocation", "Operational efficiency analysis", "Risk mitigation frameworks"],
  badge: "02 / STRATEGY",
  badgeVariant: "secondary",
  icon: ChartBar,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1619657/img/seo_dashboard_analytics.png",
  cta: "Request Inquiry",
  link: "/StrategicInquiriesPage"
}, {
  id: "infrastructure",
  title: "Digital Infrastructure",
  description: "Building the robust technical foundation your organization requires to thrive. From cloud architecture to secure data governance, we ensure your infrastructure is resilient, modern, and fully optimized.",
  features: ["Secure cloud architecture", "Data governance & compliance", "Network resilience protocols", "Legacy system modernization"],
  badge: "03 / FOUNDATION",
  badgeVariant: "destructive",
  icon: Zap,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1619657/img/fast_launch_mobile.png",
  cta: "Strengthen Foundation",
  link: "/TheFoundationPage"
}, {
  id: "oversight",
  title: "Technical Oversight",
  description: "Comprehensive management of your technology stack. We provide specialized oversight to ensure your digital tools are not just functional, but are actively driving your business objectives forward.",
  features: ["Vendor management & auditing", "Technical project management", "Security posture assessment", "Innovation & R&D leadership"],
  badge: "04 / GOVERNANCE",
  badgeVariant: "outline",
  icon: RefreshCw,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1619657/img/redesign_before_after.png",
  cta: "View Domains",
  link: "/OptimizationStrategyPage"
}];
export default function DigitalGrowthSolutions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="43020" id="advisory-domains" ref={ref} className="bg-[#0a0c10] relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#00ffff]/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#475569]/10 rounded-full blur-[100px]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Badge variant="outline" className="mb-4 border-[#00ffff]/30 text-[#00ffff] bg-[#00ffff]/5 font-mono tracking-widest">
            TECHNICAL METADATA: ADVISORY
          </Badge>
          <h2 className="text-4xl font-bold tracking-tighter text-white sm:text-5xl mb-6 font-sans">
            Specialized Domains of <span className="text-[#00ffff]">Strategic Oversight</span>
          </h2>
          <p className="text-lg text-[#94a3b8] leading-relaxed font-sans">
            Century Pointe Holdings provides technical oversight and high-level consulting to help organizations 
            streamline digital workflows and internal infrastructures with architectural precision.
          </p>
        </div>

        <div className="flex flex-col gap-24 lg:gap-32">
          {services.map((service, index) => {
          const isEven = index % 2 === 0;
          return <div key={service.id} data-index={index} className={`flex flex-col lg:flex-row items-center gap-12 lg:gap-20 ${!isEven ? "lg:flex-row-reverse" : ""}`}>
                {}
                <div className="flex-1 w-full lg:w-1/2 space-y-8">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-[#00ffff]/10 text-[#00ffff] ring-1 ring-[#00ffff]/20">
                        <service.icon className="w-6 h-6" />
                      </div>
                      {service.badge && <Badge variant={service.badgeVariant as any} className="text-[10px] px-2.5 py-0.5 font-mono tracking-wider bg-[#1e293b] text-[#e2e8f0] border-[#334155]">
                          {service.badge}
                        </Badge>}
                    </div>
                    
                    <h3 className="text-3xl font-bold text-white font-sans tracking-tight">
                      {service.title}
                    </h3>
                    
                    <p className="text-[#94a3b8] text-lg leading-relaxed font-sans">
                      {service.description}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {service.features.map((feature, idx) => <div key={idx} className="flex items-start gap-3 group">
                        <div className="mt-1 p-0.5 rounded-full bg-[#00ffff]/10 text-[#00ffff] group-hover:bg-[#00ffff] group-hover:text-[#0a0c10] transition-colors duration-300">
                          <Check className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-sm font-medium text-[#cbd5e1] font-sans">
                          {feature}
                        </span>
                      </div>)}
                  </div>

                  <div className="pt-4">
                    <Button asChild size="lg" className="group bg-[#00ffff] text-[#0a0c10] hover:bg-[#00ffff]/90 shadow-[0_0_20px_rgba(0,255,255,0.3)] rounded-sm font-sans font-bold">
                      <Link to={service.link}>
                        {service.cta}
                        <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </div>

                {}
                <div className="flex-1 w-full lg:w-1/2">
                  <Card className="overflow-hidden border-[#334155]/50 bg-[#111827]/30 backdrop-blur-sm shadow-2xl ring-1 ring-white/5">
                    <CardContent className="p-0 relative aspect-[4/3] lg:aspect-square xl:aspect-[4/3]">
                      <div className="absolute inset-0 bg-gradient-to-tr from-[#0a0c10]/90 via-transparent to-transparent z-10" />
                      <img src={service.image} alt={`${service.title} technical illustration`} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105 opacity-80" loading="lazy" />
                      
                      {}
                      <div className={`absolute bottom-6 ${isEven ? "right-6" : "left-6"} z-20 max-w-[200px] hidden sm:block`}>
                        <div className="bg-[#0a0c10]/90 backdrop-blur-md border border-[#334155]/50 p-4 rounded-sm shadow-lg">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-2 h-2 rounded-full bg-[#00ffff] animate-pulse" />
                            <span className="text-[10px] font-mono font-semibold text-[#e2e8f0] uppercase tracking-widest">System Active</span>
                          </div>
                          <div className="h-1 w-full bg-[#1e293b] rounded-full overflow-hidden">
                            <div className="h-full bg-[#00ffff] w-[92%]" />
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-[9px] text-[#94a3b8] font-mono">Efficiency</span>
                            <span className="text-[9px] font-medium text-[#00ffff] font-mono">92%</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}